import { useNavigate } from 'react-router-dom';
import { LeftArrow, TrashCan } from '../../icons/SvgIcons';
import { useAppSelector } from '../../hooks/redux';
import ModalWarning from '../ui/Modal/ModalWarning';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  setPendingNavigation,
  setShowWarningModal
} from '../../store/slices/accountSlice';
import {
  changeAssessment,
  deleteAiSystem
} from '../../services/AISystemsService';
import { PATHS } from '../../constants/Paths.constants';
import { Steps } from '../steps/Steps';
import {
  resetStepper,
  setDisableNextStep,
  setIsChangingAssessment,
  setPreStepPart,
  setShowPreStep,
  setStartAiSystemBodyRequest,
  setStartAiSystemBodyResponse,
  setStepType
} from '../../store/slices/stepperSlice';
import { setAISystemStatus } from '../../store/slices/aiSystemsSlice';
import {
  resetAIPowered,
  isAnalyzingDescription,
  switchToAIClassificationSuccess
} from '../../store/slices/aiPoweredSlice';
import { H2 } from '../ui/H2/H2';
import { TextButton } from '../ui/Button/Buttons';
import {
  AssessmentType,
  ChangeAssessmentRequest
} from '../../types/AISystems.types';
import ModalSwitchAssessment from '../ui/Modal/ModalSwitchAssessment';
import { useAIClassification } from '../../hooks/useAIClassification';

type CardOption = {
  label: string;
  description: string;
  contextTransfer: boolean;
};

export function QuestionnaireHeader() {
  const { showWarningModal, pendingNavigation } = useAppSelector(
    (state) => state.account
  );
  const {
    showPreStep,
    aiSystemId,
    preStepPart,
    startAiSystemBodyRequest,
    startAiSystemBodyResponse,
    isChangingAssessment,
    isFinalizingAIReport
  } = useAppSelector((state) => state.stepper);

  const isConversationalAIEnabled = false; // hide switch to AI-powered assessment button until feature is ready for release

  const { aiPowered } = useAIClassification(aiSystemId);

  const analyzingDescription = useAppSelector(isAnalyzingDescription);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [modalType, setModalType] = useState<'back' | 'delete' | null>(null);
  const [modalTitle, setModalTitle] = useState('');
  const [modalDescription, setModalDescription] = useState<
    React.JSX.Element | string
  >('');
  const [modalButtonText, setModalButtonText] = useState('');
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<CardOption | null>(null);

  const currentAssessmentType =
    startAiSystemBodyResponse?.assessmentType ??
    startAiSystemBodyRequest.assessmentType;

  const showSteps = !showPreStep && currentAssessmentType === 'QUESTIONNAIRE';
  const showHeader =
    (preStepPart !== 'basicInformation' || !showPreStep) &&
    !analyzingDescription &&
    !isChangingAssessment &&
    !isFinalizingAIReport &&
    !aiPowered?.error &&
    isConversationalAIEnabled;

  const setBackModal = () => {
    setModalType('back');
    setModalTitle('Exit Add AI System wizard?');
    setModalDescription(
      'Are you sure you want to exit? Any unsaved progress will be lost.'
    );
    setModalButtonText('Exit Wizard');
  };

  const handleButton = (button: 'back' | 'delete') => {
    dispatch(setShowWarningModal(true));

    if (button === 'back' || pendingNavigation) {
      setBackModal();
    }

    if (button === 'delete') {
      setModalType('delete');
      setModalTitle('Delete Ai System?');
      setModalDescription(
        <p>
          <span className="font-semibold">
            Are you sure you want to delete this AI System?
          </span>{' '}
          All information you’ve entered will be permanently removed.
        </p>
      );
      setModalButtonText('Delete');
    }
  };

  const handleConfirm = async () => {
    if (modalType === 'delete') {
      await deleteAiSystem(aiSystemId);
      dispatch(setShowWarningModal(false));
      dispatch(setAISystemStatus(null));
      navigate(PATHS.AI_SYSTEMS);
    }

    if (pendingNavigation) {
      navigate(pendingNavigation);
      dispatch(setPendingNavigation(null));
    } else {
      navigate(PATHS.AI_SYSTEMS);
    }

    dispatch(resetStepper());
    dispatch(setStepType(''));
    dispatch(setDisableNextStep(true));
    dispatch(setShowWarningModal(false));
  };

  const handleChangeAssessment = async () => {
    const newAssessmentType: AssessmentType =
      currentAssessmentType === 'QUESTIONNAIRE'
        ? 'AI_POWERED'
        : 'QUESTIONNAIRE';

    if (newAssessmentType === 'AI_POWERED' && !selectedOption) return;

    const requestBody: ChangeAssessmentRequest = {
      regulationCode: 'EU_AI_ACT',
      assessmentType: newAssessmentType,
      contextTransfer: selectedOption?.contextTransfer ?? false
    };

    try {
      dispatch(setIsChangingAssessment(true));
      setShowSwitchModal(false);
      const response = await changeAssessment(aiSystemId, requestBody);
      dispatch(
        setStartAiSystemBodyRequest({
          ...startAiSystemBodyRequest,
          assessmentType: newAssessmentType
        })
      );

      if (response.assessmentType === 'QUESTIONNAIRE') {
        dispatch(setStartAiSystemBodyResponse(response));
        dispatch(setPreStepPart('entityClassification'));
        dispatch(setShowPreStep(true));
        dispatch(setDisableNextStep(true));
        dispatch(resetAIPowered());
      } else {
        dispatch(switchToAIClassificationSuccess(response));
        dispatch(setShowPreStep(false));
        dispatch(setDisableNextStep(true));

        if (startAiSystemBodyResponse) {
          dispatch(
            setStartAiSystemBodyResponse({
              ...startAiSystemBodyResponse,
              assessmentType: response.assessmentType
            })
          );
        }
      }

      setSelectedOption(null);
    } catch (error) {
      console.error('Failed to change assessment type:', error);
    } finally {
      dispatch(setIsChangingAssessment(false));
    }
  };

  useEffect(() => {
    if (pendingNavigation) setBackModal();
  }, [pendingNavigation]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => handleButton('back')}
          className="flex gap-2 items-center"
        >
          {LeftArrow()}
          <span className="text-gray-900 font-medium">Add AI System</span>
        </button>
        {(preStepPart !== 'basicInformation' || !showPreStep) && (
          <button
            type="button"
            id="questionnaire-header-delete-button"
            data-testid="questionnaire-header-delete-button"
            onClick={() => handleButton('delete')}
          >
            {TrashCan('small')}
          </button>
        )}
      </div>

      {showSteps && <Steps />}

      {showHeader && (
        <div className="flex items-center justify-between mx-12 my-5">
          <H2
            title={`${currentAssessmentType === 'AI_POWERED' ? 'AI-Powered' : 'Questionnaire'} Assessment`}
            textSize="text-base"
          />
          <TextButton
            onClick={() => {
              setShowSwitchModal(true);
            }}
            className="text-xs font-medium h-[30px]"
          >
            Switch To{' '}
            {currentAssessmentType === 'AI_POWERED'
              ? 'Questionnaire'
              : 'AI-Powered'}{' '}
            Assessment
          </TextButton>
        </div>
      )}

      <ModalSwitchAssessment
        assessmentType={currentAssessmentType as AssessmentType}
        onClick={handleChangeAssessment}
        showModal={showSwitchModal}
        setShowModal={setShowSwitchModal}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />

      {showWarningModal && (
        <ModalWarning
          title={modalTitle}
          description={modalDescription}
          buttonText={modalButtonText}
          handleButton={handleConfirm}
        />
      )}
    </div>
  );
}
